import React, { FC } from 'react'
import { TooltipProps } from 'react-tooltip'
import styled, { useTheme } from 'styled-components'
import { H5 } from 'ui/heading'
import { HoverGallery } from 'ui/hover-gallery'
import { LabelProps } from 'ui/label'

import { CarData, CarViewSettings } from '../../../features/car/types'
import { useListSmartContractAPI } from '../../../features/smart-contract/list/useListSmartContractAPI'
import { getProjectName } from '../../../lib/get-project-name'
import { Icon } from '../../icon'
import { Dealer } from './Dealer'
import { Labels } from './Labels'
import { CreditPrice, EmptyPrice, Price } from './Price'
import { Specifications } from './Specifications'
import { Status } from './Status'

export type Props = {
  carData: CarData
  carViewSettings: CarViewSettings
  smartContractEnabled: boolean
  onClick?: (evt: any) => void
  linkTarget?: '_self' | '_parent' | '_top' | '_blank'
  showCreditPrice?: boolean
  tooltipSettings?: TooltipProps
  showVideoIcon?: boolean
}

export const Card: FC<Props> = ({
  onClick,
  carViewSettings,
  carData,
  smartContractEnabled,
  linkTarget = '_self',
  showCreditPrice = false,
  tooltipSettings,
  showVideoIcon = false
}) => {
  const {
    labels,
    baseSpecifications,
    images,
    title,
    year,
    id,
    vin,
    priceWithDiscounts,
    price,
    status,
    location,
    url,
    creditPayment,
    cities,
    dealers,
    reportVIN,
    has_autoteka_report
  } = carData

  const {
    hideDealer,
    hideStatus,
    price: { hidePrices, disclaimer, noPriceText },
    isMoreAboutPrice,
    isShowLabel,
    isShowReportVIN
  } = carViewSettings

  const expandedLabels: LabelProps[] | undefined = labels?.map((label) => {
    label.carId = id
    return label
  })

  const pricesAreNull = (price === null && priceWithDiscounts === null) || priceWithDiscounts === 0

  const { state, api } = useListSmartContractAPI()

  let infoForOpenWidget = state?.cars.find((item) => item.carData.cardId === id)

  const theme = useTheme()

  return (
    <Container as="a" href={url} onClick={onClick} id={id} className={'avn105-00__card'} target={linkTarget}>
      <Box>
        {!!expandedLabels?.length && isShowLabel && (
          <Labels labels={expandedLabels} tooltipSettings={tooltipSettings} />
        )}
        <GalleryWrapper>
          <HoverGallery
            slides={images}
            maxSlides={carViewSettings.maxPhotosInPreview || 5}
            i18n={{ moreSlidesForms: carViewSettings.i18n.morePhotosForms }}
            showPhotosOverflow={carViewSettings.showTextWhenThereIsMorePhotos}
          />
          <WrapIcon>
            {has_autoteka_report && isShowReportVIN && <ReportVinIcon>Отчет по VIN</ReportVinIcon>}
            {showVideoIcon && (
              <VideoIconWrapper>
                <Icon name="video-play2" size={1.4} />
              </VideoIconWrapper>
            )}
          </WrapIcon>
        </GalleryWrapper>
        <Content>
          <TitleWrapper>
            <H5 weight={theme?.textStyles?.h5?.fontWeight as any}>{title}</H5>
          </TitleWrapper>

          {(year || vin) && (
            <SubTitle>
              {getProjectName() !== 'avito' && <Year>{year !== '0' ? year : null}</Year>}
              <Id>{vin}</Id>
            </SubTitle>
          )}

          {baseSpecifications?.length ? (
            <SpecificationsWrapper>
              <Specifications specifications={baseSpecifications} />
            </SpecificationsWrapper>
          ) : null}

          {status && !hideStatus && (
            <StatusWrapper>
              <Status color={status.color} text={status.text} />
            </StatusWrapper>
          )}

          {!pricesAreNull && !hidePrices && (
            <Wrap className={getProjectName() === 'avito' ? 'avito_style' : ''}>
              {showCreditPrice && creditPayment ? (
                <CreditPrice value={creditPayment} showRange={carViewSettings.price.showAsRange} />
              ) : (
                <Price
                  skeleton={!price && smartContractEnabled}
                  higher={price}
                  lower={priceWithDiscounts}
                  alwaysForceRange={carViewSettings.price.alwaysForceRange}
                  showRange={carViewSettings.price.showAsRange}
                  crossPrice={carViewSettings.price.crossPrice}
                  smartContractEnabled={smartContractEnabled}
                  disclaimer={disclaimer}
                />
              )}
            </Wrap>
          )}
          {pricesAreNull && !hidePrices && noPriceText && <EmptyPrice>{noPriceText}</EmptyPrice>}

          {isMoreAboutPrice && getProjectName() !== 'avito' && (
            <MoreAboutPrice
              onClick={(e) => {
                e.preventDefault()
                api.openWidget({
                  car: infoForOpenWidget?.carData,
                  preset: 'vin',
                  currency: infoForOpenWidget?.currency,
                  buttonText: 'Подробнее о цене',
                  dealershipData: infoForOpenWidget?.dealershipData,
                  dontOpenWidget: false
                })
              }}
            >
              <Icon name={'info'} size={0.8} />
              <span>Подробнее о цене</span>
            </MoreAboutPrice>
          )}

          {location && !hideDealer && getProjectName() !== 'avito' && (
            <Dealer
              text={location}
              groupingCities={cities}
              showLocationAs={carViewSettings.showLocationAs}
              longNameDealers={dealers.length === 1 ? dealers[0].name : ''}
            />
          )}
          {getProjectName() === 'avito' && <PricesText>Цена не поменяется в салоне</PricesText>}
        </Content>
      </Box>
    </Container>
  )
}

const Container = styled.section`
  color: unset;
  display: inline-block;
  text-decoration: unset;
  cursor: pointer;
  height: 100%;
  width: 100%;
  position: relative;

  &:last-of-type {
    margin-right: 0;
  }
`

const Box = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.palette.white};
  color: ${({ theme }) => theme.palette.grey[900]};
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  text-decoration: none;
  transition: box-shadow 0.2s;
  position: relative;
  border-radius: ${() => getProjectName() === 'avito' && '12px'};

  &:hover {
    box-shadow: 0 0.2rem 1rem 0 rgba(0, 0, 0, 0.1);
  }
`

const GalleryWrapper = styled.div`
  position: relative;
  border-radius: ${() => getProjectName() === 'avito' && '12px'};
`

const Content = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]} ${theme.spacing[3]}`};
  ${({ theme }) => theme.textStyles.p3};

  background: ${({ theme }) => getProjectName() === 'mercedes-benz' && theme.palette.grey[900]};
  color: ${({ theme }) => getProjectName() === 'mercedes-benz' && theme.palette.white};
`

const TitleWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`

const SubTitle = styled.div`
  color: ${({ theme }) => theme.palette.grey[500]};
  display: flex;
  justify-content: ${() => (getProjectName() === 'avito' ? 'auto' : 'space-between')};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  margin-top: auto;
  color: ${({ theme }) => getProjectName() === 'mercedes-benz' && theme.palette.white};
`

const Year = styled.span`
  color: ${({ theme }) => theme.palette.grey[700]};
  color: ${({ theme }) => getProjectName() === 'mercedes-benz' && 'inherit'};
  margin-right: ${({ theme }) => theme.spacing[2]};
`

const Id = styled.span`
  margin-left: ${({ theme }) => (getProjectName() === 'avito' ? 0 : theme.spacing[2])};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  ${({ theme }) =>
    getProjectName() === `avito` ? `font-weight: 400; font-size: 14px; line-height: 16px;` : theme.textStyles.p3};
  color: ${({ theme }) => theme.palette.grey[700]};
  color: ${({ theme }) => getProjectName() === 'mercedes-benz' && 'inherit'};
`

const SpecificationsWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`

const StatusWrapper = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  color: ${({ theme }) => getProjectName() === 'mercedes-benz' && theme.palette.white};
`

const Prices = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`

const PricesText = styled.div`
  color: ${({ theme }) => theme.palette.grey[700]};
  font-size: 14px;
`

const MoreAboutPrice = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.brand.primary};
  color: ${({ theme }) => getProjectName() === 'mercedes-benz' && theme.palette.white};

  margin-bottom: 15px;
  margin-top: 4px;
  span {
    margin-left: 5px;
  }
  &:hover span {
    text-decoration: underline;
  }
`

const VideoIconWrapper = styled.div`
  color: ${({ theme }) => theme.palette.brand.primary};
  margin-left: 4px;
`

const Wrap = styled.div`
  &.avito_style {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`

const ReportVinWrapper = styled.div`
  margin-right: 10px;
`

const WrapIcon = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  right: ${({ theme }) => theme.spacing[4]};
  bottom: ${({ theme }) => theme.spacing[2]};
`

const ReportVinIcon = styled.div`
  ${({ theme }) => theme.typography.paragraph.p2}
  width: 67px;
  height: 22px;
  border-radius: 6px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  background-color: ${({ theme }) => theme.palette.brand.primary};
  margin-bottom: 0;
`
